"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@/lib/auth";
import {
  findCompanyOrg,
  findPersonalOrg,
  isOrgAdminRole,
  type UserOrg,
} from "@/lib/orgs";

export const ACTIVE_ORG_STORAGE_KEY = "mc_active_org_id";

type ActiveOrgState = {
  /** True after first resolve against user.orgs */
  ready: boolean;
  orgs: UserOrg[];
  activeOrg: UserOrg | null;
  activeOrgId: number | null;
  isCompany: boolean;
  isPersonal: boolean;
  isAdmin: boolean;
  setActiveOrgId: (orgId: number) => void;
};

const ActiveOrgContext = createContext<ActiveOrgState | null>(null);

function readStoredOrgId(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
    if (!raw) return null;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

function writeStoredOrgId(orgId: number) {
  try {
    localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, String(orgId));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Default when nothing stored: company membership, else personal, else first. */
export function defaultOrg(orgs: UserOrg[]): UserOrg | null {
  if (!orgs.length) return null;
  return findCompanyOrg(orgs) ?? findPersonalOrg(orgs) ?? orgs[0] ?? null;
}

export function resolveActiveOrg(orgs: UserOrg[], storedId: number | null): UserOrg | null {
  if (!orgs.length) return null;

  // Always prioritize company org — if the user belongs to a company,
  // default to it unless they explicitly stored a *different company* org.
  const companyOrg = findCompanyOrg(orgs);

  if (storedId != null) {
    const stored = orgs.find((o) => o.id === storedId);
    if (stored) {
      // Honor stored choice only if it's a company org, or no company exists
      if (stored.account_type === "company" || !companyOrg) {
        return stored;
      }
    }
  }

  // Default: company first, then personal, then first
  return companyOrg ?? findPersonalOrg(orgs) ?? orgs[0] ?? null;
}

export function ActiveOrgProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const orgs = useMemo<UserOrg[]>(
    () =>
      (user?.orgs || []).map((o) => ({
        id: o.id,
        name: o.name,
        role: o.role,
        account_type: o.account_type ?? "individual",
        logo_url: o.logo_url ?? null,
      })),
    [user?.orgs],
  );

  const [activeOrgId, setActiveOrgIdState] = useState<number | null>(null);
  const [ready, setReady] = useState(false);

  // Resolve stored / default org whenever memberships change
  useEffect(() => {
    if (!user) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- deriving org context state from localStorage/memberships, not available during SSR
      setActiveOrgIdState(null);
      setReady(false);
      return;
    }
    const stored = readStoredOrgId();
    const resolved = resolveActiveOrg(orgs, stored);
    if (resolved) {
       
      setActiveOrgIdState(resolved.id);
      if (stored !== resolved.id) writeStoredOrgId(resolved.id);
    } else {
      setActiveOrgIdState(null);
    }
    setReady(true);
  }, [user, orgs]);

  const setActiveOrgId = useCallback(
    (orgId: number) => {
      if (!orgs.some((o) => o.id === orgId)) return;
      setActiveOrgIdState(orgId);
      writeStoredOrgId(orgId);
      window.dispatchEvent(
        new CustomEvent("mc:active-org-changed", { detail: { orgId } }),
      );
    },
    [orgs],
  );

  const activeOrg = useMemo(
    () => orgs.find((o) => o.id === activeOrgId) ?? null,
    [orgs, activeOrgId],
  );

  const value = useMemo<ActiveOrgState>(
    () => ({
      ready,
      orgs,
      activeOrg,
      activeOrgId,
      isCompany: (activeOrg?.account_type || "") === "company",
      isPersonal: (activeOrg?.account_type || "individual") === "individual",
      isAdmin: isOrgAdminRole(activeOrg?.role),
      setActiveOrgId,
    }),
    [ready, orgs, activeOrg, activeOrgId, setActiveOrgId],
  );

  return (
    <ActiveOrgContext.Provider value={value}>{children}</ActiveOrgContext.Provider>
  );
}

export function useActiveOrg(): ActiveOrgState {
  const ctx = useContext(ActiveOrgContext);
  if (!ctx) throw new Error("useActiveOrg must be used within ActiveOrgProvider");
  return ctx;
}

/** Optional hook when provider may be absent (e.g. marketing). */
export function useActiveOrgOptional(): ActiveOrgState | null {
  return useContext(ActiveOrgContext);
}
